import React, { useContext, useMemo, useState } from 'react';
import { style } from 'typestyle';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import * as listStyles from '../../../core/admin-list.styles';
import LinkedOfficeChips from '../../../core/components/linked-office-chips/linked-office-chips.component';
import EditFacilityModal from '../modals/edit-facility-modal.component';
import FacilityIdentity from './facility-identity.component';
import type Facility from '../models/facility';
import type Office from '../../offices/models/office';
import { IsMobileContext } from '../../../core/context/is-mobile';

/**
 * `row` is the flush, full-width list row (office chips ride along the right);
 * `grid` is the bordered card used by the grid view, where the chips sit underneath.
 */
export type FacilityCardVariant = 'row' | 'grid';

interface Props {
  readonly initialFacility: Facility;
  readonly allOffices?: Office[];
  readonly variant?: FacilityCardVariant;
  readonly onDelete: (value: Facility) => void;
  readonly handleChange: (
    originalFacility: Facility,
    changedFacility: Facility,
  ) => Promise<void>;
  readonly facilityIsValid: (value: Facility) => boolean;
}

const FacilityCard = (
  {
    initialFacility,
    allOffices,
    variant = 'row',
    onDelete,
    handleChange,
    facilityIsValid,
  }: Props,
): React.JSX.Element => {
  const [showEditFacilityModal, setShowEditFacilityModal] = (
    useState(false)
  );
  const isMobile = useContext(IsMobileContext);

  const linkedOfficeNames = useMemo(
    () => (allOffices ?? [])
      .filter(
        (o) => initialFacility.officeIds.includes(o.id ?? ''),
      )
      .map((o) => o.name),
    [allOffices, initialFacility.officeIds],
  );

  const openEditModal = (): void => {
    setShowEditFacilityModal(true);
  };

  // Opening the editor has to work from the keyboard too, so the row behaves like the button it is.
  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openEditModal();
    }
  };

  const chevron = (
    <span className={style(listStyles.rowGo)}>
      <ChevronRightIcon size={20} />
    </span>
  );

  return (
    <>
      {variant === 'grid' ? (
        <div
          className={style(listStyles.gridCard)}
          key={initialFacility.name}
          role="button"
          tabIndex={0}
          onClick={openEditModal}
          onKeyDown={handleKeyDown}
        >
          <div className={style(listStyles.gridCardTop)}>
            <FacilityIdentity facility={initialFacility} />
            {chevron}
          </div>
          <LinkedOfficeChips officeNames={linkedOfficeNames} maxVisible={1} />
        </div>
      ) : (
        <div
          className={style(listStyles.flushRow)}
          key={initialFacility.name}
          role="button"
          tabIndex={0}
          onClick={openEditModal}
          onKeyDown={handleKeyDown}
        >
          <FacilityIdentity facility={initialFacility} />
          {/* On narrow screens the row keeps only the essentials; the chips are the first to go. */}
          <div className={style(listStyles.rowLinks(isMobile))}>
            <LinkedOfficeChips officeNames={linkedOfficeNames} maxVisible={2} inline />
          </div>
          {chevron}
        </div>
      )}
      <EditFacilityModal
        showModal={showEditFacilityModal}
        handleClose={(): void => {
          setShowEditFacilityModal(false);
        }}
        facility={initialFacility}
        handleSave={handleChange}
        onDelete={onDelete}
        facilityIsValid={facilityIsValid}
      />
    </>
  );
};

export default FacilityCard;
